import { NonceType } from './../../models/nonce-type.enum';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import * as fromRoot from 'app/core/store';
import { BookingState } from 'app/core/store/reducers/booking.reducer';
import { AuthenticationState } from 'app/shared/authentication/models/login-state.enum';
import { AuthService } from 'app/shared/authentication/services/auth.service';
import { Identifier } from 'app/shared/storage/services/local-storage.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Nonce } from '../../models/nonce.model';
import { NonceClient } from '../../models/nonce-client.model';

enum Mode {
  EnterEmail = 1,
  EnterCode = 2,
  SelectAccount = 3,
}

@Component({
  selector: 'core-auth-email-confirmation',
  templateUrl: './email-confirmation.component.html',
  styleUrls: ['./email-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailConfirmationComponent implements OnDestroy, OnInit {
  @Input() canReturn = false;
  @Input() previousState!: AuthenticationState;
  @Input() previousCanReturn!: boolean;
  @Output() loginSelectionEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() loginEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeModalEmitter: EventEmitter<any> = new EventEmitter<any>();
  emailConfirmFormGroup?: UntypedFormGroup;
  verificationCodeFormGroup?: UntypedFormGroup;
  currMode = Mode.EnterEmail;
  Mode = Mode;
  NonceType = NonceType;
  subscription: Subscription = new Subscription();
  isSubmitted = false;
  nonce?: Nonce;
  clientsWithUser: NonceClient[] = [];
  clientsWithNoUser: NonceClient[] = [];

  constructor(
    private store: Store<fromRoot.State>,
    private authService: AuthService,
    private ref: ChangeDetectorRef,
    private translateService: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fillFormGroup();
    this.handleBookingContext();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fillFormGroup(): void {
    this.emailConfirmFormGroup = new UntypedFormGroup({
      email: new UntypedFormControl(null, [
        Validators.required,
        Validators.email,
        this.validateNoPlus.bind(this),
      ]),
      otherData: new UntypedFormControl(null),
    });
    this.ref.markForCheck();
  }

  fillVerificationFormGroup(): void {
    this.verificationCodeFormGroup = new UntypedFormGroup({
      additionalInformation: new UntypedFormControl(
        this.emailConfirmFormGroup?.get('email')?.value,
        [Validators.required, Validators.email]
      ),
      verificationCode: new UntypedFormControl(null, Validators.required),
    });
    this.ref.markForCheck();
  }

  onEmailConfirmationSubmit(): void {
    this.isSubmitted = true;
    if (this.emailConfirmFormGroup?.valid) {
      this.subscription.add(
        this.authService.beginRegistration(this.emailConfirmFormGroup).subscribe(() => {
          this.fillVerificationFormGroup();
          this.isSubmitted = false;
          this.currMode = Mode.EnterCode;
          this.ref.markForCheck();
        })
      );
    }
  }

  onVerificationCodeSubmit(): void {
    this.isSubmitted = true;
    const verificationCode = this.verificationCodeFormGroup?.get('verificationCode')?.value;
    const additionalInformation =
      this.verificationCodeFormGroup?.get('additionalInformation')?.value;
    if (this.verificationCodeFormGroup?.valid) {
      this.subscription.add(
        this.authService
          .getNonceWithVerificationCode(verificationCode, additionalInformation)
          .subscribe(
            nonce => {
              this.nonce = nonce;
              this.clientsWithUser = this.nonce.data.otherData.ClientsWithUser || [];
              this.clientsWithNoUser = this.nonce.data.otherData.ClientsWithNoUser || [];
              if (this.clientsWithUser.length === 0 && this.clientsWithNoUser.length === 0) {
                this.onSelectAction(NonceType.NewAccountAndClient);
              } else {
                this.currMode = Mode.SelectAccount;
                this.ref.markForCheck();
              }
            },
            () => {
              this.ref.markForCheck();
            }
          )
      );
    }
  }

  onSelectAction(type: NonceType, entityId?: number): void {
    switch (type) {
      case NonceType.NewAccountAndClient:
        this.router
          .navigate([decodeURIComponent('/new-client-registration')], {
            queryParams: { state: this.nonce?.id },
          })
          .then();
        break;
      case NonceType.NewAccountForExistingClient:
        this.router
          .navigate([decodeURIComponent('/complete-registration')], {
            queryParams: { state: this.nonce?.id, entity: entityId },
          })
          .then();
        break;
      case NonceType.ForgottenPassord:
        this.router
          .navigate([decodeURIComponent('/reset-password')], {
            queryParams: { state: this.nonce?.id, entity: entityId },
          })
          .then();
        break;
      default:
        break;
    }
    this.closeModalEmitter.emit();
    this.ref.markForCheck();
  }

  displayRequiredError(control: AbstractControl): boolean {
    return control.hasError('required') && (control.touched || this.isSubmitted);
  }

  validateNoPlus(control: UntypedFormControl): { [key: string]: any } | null {
    return !(control.value as string)?.includes('+')
      ? null
      : {
          validateNoPlus: this.translateService.instant('validation.notAllowPlusSignInEmail'),
        };
  }

  displayPatternError(control: AbstractControl): boolean {
    return control?.hasError('validateNoPlus');
  }

  onBack(): void {
    if (this.currMode === Mode.SelectAccount) {
      this.currMode = Mode.EnterCode;
      return;
    }
    if (this.currMode === Mode.EnterCode) {
      this.currMode = Mode.EnterEmail;
      return;
    }
    if (this.previousState === AuthenticationState.LogIn) {
      this.onSwitchToLogin();
    } else {
      this.onSwitchToLoginSelection();
    }
  }

  onSwitchToLogin(): void {
    this.loginEmitter.emit();
  }

  onSwitchToLoginSelection(): void {
    this.loginSelectionEmitter.emit();
  }

  getShortName(fullName: string): string {
    if (!fullName) {
      return '';
    }
    return fullName
      .split(' ')
      .map(names => names.charAt(0))
      .join('')
      .toUpperCase();
  }

  private handleBookingContext(): void {
    const regExpServices = new RegExp('^/book-online/booking-reasons/\\d*/services/\\d*');
    const regExpPractitioners = new RegExp('^/book-online/practitioners/\\d*/booking-reasons/\\d*');
    this.subscription.add(
      this.store.pipe(select(fromRoot.getBookingState)).subscribe(bookingState => {
        if (bookingState) {
          const state = JSON.parse(JSON.stringify(bookingState));
          if (
            regExpServices.test(this.router.routerState.snapshot.url) ||
            regExpPractitioners.test(this.router.routerState.snapshot.url)
          ) {
            state.isBooking = true;
          }
          this.emailConfirmFormGroup?.get('otherData')?.setValue(this.createOtherData(state));
        }
        this.ref.markForCheck();
      })
    );
  }

  private createOtherData(state: BookingState): any {
    return {
      bookingAppSession: localStorage.getItem(Identifier.TYPE_SOURCE_SESSION_TOKEN),
      practitionerId: state.practitionerId,
      serviceId: state.serviceId,
      startDateTime: state.startDateTime,
      caseId: state.caseId,
      appointmentBookingMode: state.appointmentBookingMode,
      bookingReasonId: state.bookingReasonId,
      treatmentPlanId: state.treatmentPlanId,
      integrationId: state.integrationId,
      partnerToken: state.partnerToken,
      isBooking: state.isBooking,
      sysAppointmentDeliveryMethodId: state.sysAppointmentDeliveryMethodId,
      isForWaitingList: state.isForWaitingList,
    };
  }
}
